/**
 * Moving a pane up or down the stack.
 *
 * `ChartGroup.panes` **is** the display order — the renderer zips the array
 * against `stackFractions` by index and nothing sorts — so a move is an array
 * permutation and nothing more. `Pane.height` lives on the pane object, so a
 * height travels with the pane it belongs to for free; `heightsOf` reads the
 * array afterwards and is correct without knowing a move happened.
 *
 * **`Pane.order` is deliberately not written here.** The contract carries it and
 * `addIndicator`/`newChartGroup` write it at creation, but nothing reads it and
 * `removeIndicator` never renumbered it, so it is already non-contiguous on any
 * chart something has been removed from. Maintaining it in one of the three
 * mutation paths would make it look authoritative while still being wrong in the
 * other two — and, because it rides into `structuralKey` in `...rest`, writing
 * it would turn a restack into a WebGL teardown plus a refetch, which is the one
 * thing this whole path exists to avoid. See `docs/TODO.md`.
 *
 * Its own module rather than `add-indicator.ts`: a pane move is not about
 * indicators — the price and volume panes move too, and neither has a row in the
 * picker.
 */

import { paneWeights, stackFractions } from "@qufolio/chart-engine";
import type { ChartGroup, Pane } from "@qufolio/model";

/** Up the stack is toward index 0, which is the top band. */
export type PaneMove = -1 | 1;

/**
 * Whether the move exists, so a caller can leave the button out rather than
 * render one that does nothing — the rule the PIP's swap control follows.
 *
 * An unknown id is `false` rather than a throw: the panes can change under a
 * popup that is already open, and a stale row asking about a pane that was just
 * removed should simply have no arrows.
 */
export function canMovePane(
  panes: readonly Pane[],
  paneId: string,
  delta: PaneMove,
): boolean {
  const from = panes.findIndex((pane) => pane.id === paneId);
  if (from < 0) return false;
  const to = from + delta;
  return to >= 0 && to < panes.length;
}

/**
 * Move one pane one slot, returning the group **unchanged by identity** when the
 * move does not exist.
 *
 * Identity matters rather than being a nicety: `setPanes` replaces the array
 * wholesale and the reducer shares structure below it, so returning a fresh
 * object for a no-op would re-render every pane in the group — and, worse, mark
 * the workspace dirty and spend a save on nothing.
 */
export function movePane(group: ChartGroup, paneId: string, delta: PaneMove): ChartGroup {
  const from = group.panes.findIndex((pane) => pane.id === paneId);
  if (from < 0) return group;
  const to = from + delta;
  if (to < 0 || to >= group.panes.length) return group;

  const panes = [...group.panes];
  const [moved] = panes.splice(from, 1);
  panes.splice(to, 0, moved!);
  return { ...group, panes };
}

/**
 * Drop a pane at an arbitrary index — what a drag commits, where a grip may have
 * crossed several rows before it was released.
 *
 * `toIndex` is the slot the pane ends up in *after* it has been lifted out, so
 * dragging the top pane to the bottom is `panes.length - 1` rather than
 * `panes.length`. Clamped rather than rejected: a pointer released past the last
 * row means the last row, which is what the drop indicator was showing.
 */
export function movePaneTo(group: ChartGroup, paneId: string, toIndex: number): ChartGroup {
  const from = group.panes.findIndex((pane) => pane.id === paneId);
  if (from < 0) return group;
  const to = Math.max(0, Math.min(group.panes.length - 1, Math.trunc(toIndex)));
  if (to === from) return group;

  const panes = [...group.panes];
  const [moved] = panes.splice(from, 1);
  panes.splice(to, 0, moved!);
  return { ...group, panes };
}

/**
 * Reorder to an explicit id list.
 *
 * Ignored unless the list is exactly the current pane set — the same rule the
 * builder's `setPaneOrder` applies, and for the same reason: a partial
 * permutation would silently drop a pane from the chart rather than fail.
 */
export function reorderPanes(group: ChartGroup, paneIds: readonly string[]): ChartGroup {
  if (paneIds.length !== group.panes.length) return group;
  const byId = new Map(group.panes.map((pane) => [pane.id, pane] as const));
  const panes: Pane[] = [];
  for (const id of paneIds) {
    const pane = byId.get(id);
    // A duplicate id lands here on its second visit, because the first deleted
    // it — so this covers "unknown" and "repeated" with one check.
    if (!pane) return group;
    byId.delete(id);
    panes.push(pane);
  }
  return { ...group, panes };
}

/**
 * Which pane covers a fraction of the chart's height, or `null` outside it.
 *
 * The hover test for the on-chart arrows. It is here, and pure, because the
 * alternative is what it replaced: a band `div` per pane with `pointerEvents`
 * on, which does find the hover — and swallows every pan and zoom on the way,
 * because the canvas underneath never sees the pointer again. So the overlay
 * stays inert and the *container* is asked where the pointer is, which it can
 * answer because the event bubbles up from the canvas.
 *
 * `stackFractions` is the same function the renderer stacks with, for the reason
 * `PaneDividers` takes its seams from `paneBoundaries`: two pieces of arithmetic
 * for one layout drift apart on the first divider drag.
 */
export function paneAtFraction(panes: readonly Pane[], fraction: number): string | null {
  if (fraction < 0 || fraction > 1) return null;
  const bands = stackFractions(paneWeights(panes));
  const i = bands.findIndex((band) => fraction >= band.y && fraction < band.y + band.height);
  // The very last pixel is `fraction === 1`, which no half-open band contains.
  const pane = i >= 0 ? panes[i] : panes[panes.length - 1];
  return pane?.id ?? null;
}
